import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign, TrendingUp, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { SummaryCard } from './SummaryCard';
import { SalesProfitHistory } from '@/types/primaryLedger';

interface ColumnConfig {
  id: string;
  label: string;
  accessor: (item: SalesProfitHistory) => string;
}

interface SalesProfitSectionProps {
  data: SalesProfitHistory[];
  loading?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatPercent = (value: number): string => {
  return `${(value * 100).toFixed(2)}%`;
};

const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

const formatDate = (timestamp: any): string => {
  if (!timestamp?.toDate) return '';
  const date = timestamp.toDate();
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const SalesProfitSection: React.FC<SalesProfitSectionProps> = ({ data, loading = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<number | null>(null);
  const [visibleSeries, setVisibleSeries] = useState({
    revenue: true,
    grossProfit: true
  });

  const defaultColumns: ColumnConfig[] = [
    { id: 'date', label: 'Date', accessor: (item) => formatDate(item.date) },
    { id: 'traffic', label: 'Traffic', accessor: (item) => formatNumber(item.traffic) },
    { id: 'convRate', label: 'Conv. Rate', accessor: (item) => formatPercent(item.conversionRate) },
    { id: 'units', label: 'Units', accessor: (item) => formatNumber(item.units) },
    { id: 'revenue', label: 'Revenue', accessor: (item) => formatCurrency(item.revenue) },
    { id: 'ppcSpend', label: 'PPC Spend', accessor: (item) => formatCurrency(item.ppcSpend) },
    { id: 'grossProfit', label: 'Gross Profit', accessor: (item) => formatCurrency(item.grossProfit) },
  ];

  const [columns, setColumns] = useState<ColumnConfig[]>(defaultColumns);

  useEffect(() => {
    const saved = localStorage.getItem('sales-profit-columns');
    if (saved) {
      try {
        const savedColumnOrder: Array<{id: string}> = JSON.parse(saved);
        const restoredColumns = savedColumnOrder
          .map(savedCol => defaultColumns.find(dc => dc.id === savedCol.id))
          .filter((col): col is ColumnConfig => col !== undefined);
        
        if (restoredColumns.length > 0) {
          setColumns(restoredColumns);
        }
      } catch (e) {
        console.error('Failed to parse saved columns:', e);
      }
    }
  }, []);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedColumn(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === dropIndex) return;

    const newColumns = [...columns];
    const [removed] = newColumns.splice(draggedColumn, 1);
    newColumns.splice(dropIndex, 0, removed);
    
    setColumns(newColumns);
    localStorage.setItem('sales-profit-columns', JSON.stringify(newColumns));
    setDraggedColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const handleToggleSeries = (dataKey: 'revenue' | 'grossProfit') => {
    setVisibleSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  const CustomLegend = () => {
    const seriesConfig = [
      { key: 'revenue' as const, label: 'Revenue', color: '#10b981' },
      { key: 'grossProfit' as const, label: 'Gross Profit', color: '#3b82f6' }
    ];

    return (
      <div className="flex justify-center gap-4 mt-4">
        {seriesConfig.map((series) => {
          const isVisible = visibleSeries[series.key];
          return (
            <button
              key={series.key}
              onClick={() => handleToggleSeries(series.key)}
              className={`flex items-center gap-2 px-3 py-1 rounded transition-all hover:bg-gray-100 dark:hover:bg-gray-800 ${
                isVisible ? 'opacity-100' : 'opacity-50'
              }`}
              title={isVisible ? 'Click to hide' : 'Click to show'}
            >
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: series.color }}
              />
              <span className={`text-sm text-gray-700 dark:text-gray-300 ${!isVisible ? 'line-through' : ''}`}>
                {series.label}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm text-gray-600 dark:text-gray-400">
              <span style={{ color: entry.color }}>{entry.name}: </span>
              {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
          No sales and profit data available for the selected date range.
        </CardContent>
      </Card>
    );
  }

  const totalRevenue = data.reduce((sum, item) => sum + item.revenue, 0);
  const totalGrossProfit = data.reduce((sum, item) => sum + item.grossProfit, 0);
  const totalPPCSpend = data.reduce((sum, item) => sum + item.ppcSpend, 0);
  const totalUnits = data.reduce((sum, item) => sum + item.units, 0);
  const avgConversionRate = data.reduce((sum, item) => sum + item.conversionRate, 0) / data.length;
  const avgTraffic = data.reduce((sum, item) => sum + item.traffic, 0) / data.length;

  const chartData = [...data]
    .sort((a, b) => a.date.toMillis() - b.date.toMillis())
    .map(item => ({
      date: formatDate(item.date),
      revenue: item.revenue,
      grossProfit: item.grossProfit
    }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard
          title="Total Revenue"
          value={formatCurrency(totalRevenue)}
          icon={DollarSign}
          color="text-green-600 dark:text-green-400"
          subtitle={`${formatNumber(totalUnits)} units sold`}
        />
        <SummaryCard
          title="Total Gross Profit"
          value={formatCurrency(totalGrossProfit)}
          icon={TrendingUp}
          color="text-blue-600 dark:text-blue-400"
          subtitle={`${formatPercent(totalGrossProfit / totalRevenue || 0)} margin`}
        />
        <SummaryCard
          title="Total PPC Spend"
          value={formatCurrency(totalPPCSpend)}
          icon={Package}
          color="text-purple-600 dark:text-purple-400"
          subtitle={`${formatPercent(totalPPCSpend / totalRevenue || 0)} of revenue`}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Revenue & Gross Profit Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                stroke="#9ca3af"
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                stroke="#9ca3af"
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
              {visibleSeries.revenue && (
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#10b981"
                  strokeWidth={3}
                  name="Revenue"
                  dot={{ fill: '#10b981', r: 3 }}
                  activeDot={{ r: 5 }}
                />
              )}
              {visibleSeries.grossProfit && (
                <Line
                  type="monotone"
                  dataKey="grossProfit"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  name="Gross Profit"
                  dot={{ fill: '#3b82f6', r: 3 }}
                  activeDot={{ r: 5 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle>Sales & Profit History</CardTitle>
            {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent>
          <div className="overflow-auto max-h-96">
            <table className="w-full text-sm relative">
              <thead className="sticky top-0 bg-white dark:bg-gray-900 z-10">
                <tr className="border-b dark:border-gray-700">
                  {columns.map((col, index) => (
                    <th
                      key={col.id}
                      className={`p-2 cursor-move select-none ${index === 0 ? 'text-left' : 'text-right'} ${
                        draggedColumn === index ? 'opacity-50' : ''
                      }`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...data].sort((a, b) => b.date.toMillis() - a.date.toMillis()).map((item, rowIndex) => (
                  <tr
                    key={item.id}
                    className={`border-b dark:border-gray-800 ${rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50'}`}
                  >
                    {columns.map((col, colIndex) => (
                      <td
                        key={col.id}
                        className={`p-2 ${colIndex === 0 ? 'text-left font-medium' : 'text-right'} ${
                          col.id === 'grossProfit' ? 'font-semibold' : ''
                        }`}
                      >
                        {col.accessor(item)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default SalesProfitSection;